<div class="payment-test-container">
  <mat-card class="header-card">
    <mat-card-header>
      <mat-card-title>
        <mat-icon>payment</mat-icon>
        {{ invoiceMode ? 'Pay Invoice' : 'Payment System Test Page' }}
      </mat-card-title>
      <mat-card-subtitle>
        {{ invoiceMode ? 'Complete payment for the selected invoice' : 'Test all payment methods and functionality' }}
      </mat-card-subtitle>
    </mat-card-header>
  </mat-card>

  <!-- Invoice Information (when in invoice mode) -->
  <mat-card class="invoice-info-card" *ngIf="invoiceMode">
    <!-- Loading Spinner -->
    <div class="loading-section" *ngIf="isLoadingInvoice">
      <mat-spinner diameter="30"></mat-spinner>
      <p>Loading invoice details...</p>
    </div>
    
    <!-- Invoice Details -->
    <div *ngIf="!isLoadingInvoice && invoice">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>receipt</mat-icon>
          Invoice #INV-{{ invoice.clinicInvoiceId.toString().padStart(4, '0') }}
        </mat-card-title>
        <mat-card-subtitle>
          <button mat-icon-button 
                  [routerLink]="['/clinic/billing/invoice', invoice.clinicInvoiceId]"
                  matTooltip="View Invoice Details">
            <mat-icon>visibility</mat-icon>
          </button>
        </mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div class="invoice-summary">
          <div class="invoice-amounts">
            <div class="amount-row">
              <span class="label">Total Amount:</span>
              <span class="amount">£{{ invoice.total | number:'1.2-2' }}</span>
            </div>
            <div class="amount-row" *ngIf="invoice.amountPaid > 0">
              <span class="label">Amount Paid:</span>
              <span class="amount paid">£{{ invoice.amountPaid | number:'1.2-2' }}</span>
            </div>
            <div class="amount-row" *ngIf="invoice.creditsUsed > 0">
              <span class="label">Credits Used:</span>
              <span class="amount credits">£{{ invoice.creditsUsed | number:'1.2-2' }}</span>
            </div>
            <div class="amount-row balance-due">
              <span class="label">Balance Due:</span>
              <span class="amount due">£{{ getRemainingBalance() | number:'1.2-2' }}</span>
            </div>
          </div>
          <div class="invoice-meta">
            <p><strong>Issue Date:</strong> {{ invoice.issuedOn | date:'mediumDate' }}</p>
            <p><strong>Due Date:</strong> {{ invoice.dueOn | date:'mediumDate' }}</p>
            <p *ngIf="invoice.notes"><strong>Notes:</strong> {{ invoice.notes }}</p>
          </div>
        </div>
      </mat-card-content>
    </div>
  </mat-card>

  <mat-tab-group class="payment-tabs">
    <!-- General Payment Tab -->
    <mat-tab label="General Payment">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Process Payment (Best Method)</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <form [formGroup]="generalPaymentForm" class="payment-form">
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Invoice ID</mat-label>
                <input matInput type="number" formControlName="invoiceId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Patient ID</mat-label>
                <input matInput type="number" formControlName="patientId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Appointment ID</mat-label>
                <input matInput type="number" formControlName="appointmentId">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" step="0.01" formControlName="amount">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Currency</mat-label>
                <mat-select formControlName="currency">
                  <mat-option value="USD">USD</mat-option>
                  <mat-option value="EUR">EUR</mat-option>
                  <mat-option value="GBP">GBP</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Payment Method</mat-label>
                <mat-select formControlName="paymentMethod">
                  <mat-option *ngFor="let method of availablePaymentMethods" [value]="method.value">
                    {{ method.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Stripe Payment Method ID</mat-label>
                <input matInput formControlName="stripePaymentMethodId" placeholder="pm_1234567890">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="primary" 
                      [disabled]="generalPaymentForm.invalid || isLoading"
                      (click)="processGeneralPayment()">
                <mat-icon>send</mat-icon>
                Process Payment
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    </mat-tab>

    <!-- Stripe Payment Tab -->
    <mat-tab label="Stripe Payment">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Stripe Payment</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <form [formGroup]="stripePaymentForm" class="payment-form">
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Invoice ID</mat-label>
                <input matInput type="number" formControlName="invoiceId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Patient ID</mat-label>
                <input matInput type="number" formControlName="patientId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" step="0.01" formControlName="amount">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Payment Method ID</mat-label>
                <input matInput formControlName="paymentMethodId" placeholder="pm_1234567890">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Customer ID</mat-label>
                <input matInput formControlName="customerId" placeholder="cus_1234567890">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="primary" 
                      [disabled]="stripePaymentForm.invalid || isLoading"
                      (click)="processStripePayment()">
                <mat-icon>credit_card</mat-icon>
                Process Stripe Payment
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    </mat-tab>

    <!-- Cash Payment Tab -->
    <mat-tab label="Cash Payment">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Cash Payment</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <form [formGroup]="cashPaymentForm" class="payment-form">
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Invoice ID</mat-label>
                <input matInput type="number" formControlName="invoiceId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Patient ID</mat-label>
                <input matInput type="number" formControlName="patientId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" step="0.01" formControlName="amount">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Received By</mat-label>
                <input matInput formControlName="receivedBy">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Receipt Number</mat-label>
                <input matInput formControlName="receiptNumber">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Change Given</mat-label>
                <input matInput type="number" step="0.01" formControlName="changeGiven">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="primary" 
                      [disabled]="cashPaymentForm.invalid || isLoading"
                      (click)="processCashPayment()">
                <mat-icon>money</mat-icon>
                Process Cash Payment
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    </mat-tab>

    <!-- Card Payment Tab -->
    <mat-tab label="Card Payment">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Manual Card Payment</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <form [formGroup]="cardPaymentForm" class="payment-form">
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Invoice ID</mat-label>
                <input matInput type="number" formControlName="invoiceId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Patient ID</mat-label>
                <input matInput type="number" formControlName="patientId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" step="0.01" formControlName="amount">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Card Number</mat-label>
                <input matInput formControlName="cardNumber" placeholder="4111111111111111">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Cardholder Name</mat-label>
                <input matInput formControlName="cardHolderName">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Expiry Month</mat-label>
                <input matInput type="number" formControlName="expiryMonth" placeholder="12">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Expiry Year</mat-label>
                <input matInput type="number" formControlName="expiryYear" placeholder="2025">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>CVV</mat-label>
                <input matInput formControlName="cvv" placeholder="123">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="primary" 
                      [disabled]="cardPaymentForm.invalid || isLoading"
                      (click)="processCardPayment()">
                <mat-icon>credit_card</mat-icon>
                Process Card Payment
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    </mat-tab>

    <!-- Payment Link Tab -->
    <mat-tab label="Payment Link">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Create Payment Link</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <form [formGroup]="paymentLinkForm" class="payment-form">
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Invoice ID</mat-label>
                <input matInput type="number" formControlName="invoiceId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Patient ID</mat-label>
                <input matInput type="number" formControlName="patientId">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" step="0.01" formControlName="amount">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Return URL</mat-label>
                <input matInput formControlName="returnUrl" placeholder="https://yourclinic.com/success">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="primary" 
                      [disabled]="paymentLinkForm.invalid || isLoading"
                      (click)="createPaymentLink()">
                <mat-icon>link</mat-icon>
                Create Payment Link
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    </mat-tab>

    <!-- Utilities Tab -->
    <mat-tab label="Utilities">
      <mat-card class="tab-card">
        <mat-card-header>
          <mat-card-title>Payment Utilities</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <!-- Check Payment Status -->
          <div class="utility-section">
            <h3>Check Payment Status</h3>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Transaction ID</mat-label>
                <input matInput [(ngModel)]="transactionIdForStatus" placeholder="txn_1234567890">
              </mat-form-field>
              <button mat-raised-button color="accent" 
                      [disabled]="!transactionIdForStatus || isLoading"
                      (click)="checkPaymentStatus()">
                <mat-icon>search</mat-icon>
                Check Status
              </button>
            </div>
          </div>

          <mat-divider></mat-divider>

          <!-- Process Refund -->
          <div class="utility-section">
            <h3>Process Refund</h3>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Transaction ID</mat-label>
                <input matInput [(ngModel)]="transactionIdForRefund" placeholder="txn_1234567890">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Refund Amount (optional)</mat-label>
                <input matInput type="number" step="0.01" [(ngModel)]="refundAmount" placeholder="Leave empty for full refund">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Refund Reason</mat-label>
                <input matInput [(ngModel)]="refundReason" placeholder="Patient cancellation">
              </mat-form-field>
            </div>
            <div class="button-row">
              <button mat-raised-button color="warn" 
                      [disabled]="!transactionIdForRefund || isLoading"
                      (click)="processRefund()">
                <mat-icon>undo</mat-icon>
                Process Refund
              </button>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </mat-tab>
  </mat-tab-group>

  <!-- Response Display -->
  <mat-card class="response-card" *ngIf="lastResponse">
    <mat-card-header>
      <mat-card-title>
        <mat-icon [ngClass]="lastResponse.success ? 'success-icon' : 'error-icon'">
          {{ lastResponse.success ? 'check_circle' : 'error' }}
        </mat-icon>
        Last Response
      </mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <pre class="response-json">{{ lastResponse | json }}</pre>
    </mat-card-content>
  </mat-card>

  <!-- Loading Spinner -->
  <div class="loading-overlay" *ngIf="isLoading">
    <mat-spinner></mat-spinner>
    <p>Processing payment...</p>
  </div>
</div> 